import {
  getAssetEvaluationDetail,
  getAssetEvaluationDraft,
  getAssetEvaluationList,
  getAssetEvaluationOptions,
  saveAssetEvaluationDraft,
  submitAssetEvaluation
} from '@/api/user';

const STATUS_LABEL_MAP = {
  draft: '草稿',
  pending: '待审核',
  approved: '已通过',
  rejected: '已驳回'
};

const DEFAULT_OPTIONS = {
  vertical_track: [
    { value: 'beauty_care', label: '美妆护肤' },
    { value: 'fashion_apparel', label: '服饰穿搭' },
    { value: 'food_beverage', label: '食品饮料' },
    { value: 'digital', label: '3C数码' },
    { value: 'home_living', label: '家居生活' },
    { value: 'maternity_child', label: '母婴育儿' },
    { value: 'education', label: '教育知识' },
    { value: 'sports_outdoors', label: '运动户外' },
    { value: 'gaming', label: '游戏电竞' }
  ],
  mcn_platform: [
    { value: 'douyin', label: '抖音' },
    { value: 'kuaishou', label: '快手' },
    { value: 'wechat_video', label: '视频号' },
    { value: 'xiaohongshu', label: '小红书' },
    { value: 'taobao_live', label: '淘宝直播' },
    { value: 'bilibili', label: 'B站' },
    { value: 'youtube', label: 'YouTube' },
    { value: 'tiktok', label: 'TikTok' }
  ],
  business_scope: [
    { value: 'live_commerce', label: '直播带货' },
    { value: 'content', label: '内容创作' },
    { value: 'artist_brokerage', label: '艺人经纪' },
    { value: 'brand_operation', label: '品牌代运营' },
    { value: 'knowledge', label: '知识付费' },
    { value: 'ip_authorization', label: 'IP授权' }
  ],
  qualification: [
    { value: 'performance_license', label: '营业性演出许可证' },
    { value: 'network_culture_license', label: '网络文化经营许可证' },
    { value: 'icp_license', label: 'ICP许可证' },
    { value: 'radio_tv_production', label: '广播电视节目制作' }
  ],
  account_ownership_type: [
    { value: 'mcn_owned', label: 'MCN机构名下' },
    { value: 'streamer_authorized', label: '主播个人名下（有授权）' },
    { value: 'jointly_owned', label: 'MCN与主播共有' },
    { value: 'affiliate_owned', label: '关联公司名下' }
  ]
};

function cloneDeep(value) {
  return JSON.parse(JSON.stringify(value));
}

function getResponseData(response) {
  if (!response || typeof response !== 'object') {
    return response;
  }

  if (Object.prototype.hasOwnProperty.call(response, 'meta')) {
    return response;
  }

  return Object.prototype.hasOwnProperty.call(response, 'data') ? response.data : response;
}

function createOptionList(items) {
  return (Array.isArray(items) ? items : []).map(item => ({ ...item }));
}

function getFilenameFromUrl(url) {
  if (!url) {
    return '';
  }

  const cleanUrl = String(url).split('?')[0];
  const segments = cleanUrl.split('/');
  return segments[segments.length - 1] || cleanUrl;
}

function normalizeFileList(urls) {
  return Array.isArray(urls)
    ? urls.filter(Boolean).map(url => ({ name: getFilenameFromUrl(url), url }))
    : [];
}

function extractFileUrls(files) {
  return (Array.isArray(files) ? files : []).map(item => item && item.url).filter(Boolean);
}

function formatDateTime(value) {
  if (!value) {
    return '--';
  }

  return String(value).replace('T', ' ').slice(0, 16);
}

function normalizeAnchorMetric(item, index) {
  const source = item && typeof item === 'object' ? item : {};
  return {
    id: source.id || `streamer-${index}-${Date.now()}`,
    streamerProfileId: source.streamer_profile_id || '',
    platformAccounts: source.platform_id || '',
    anchorName: source.name || '',
    totalFans: source.total_fans || '',
    totalViews: source.total_clicks || '',
    liveSessions: source.last_year_live_sessions || '',
    annualProfit: source.last_year_profit || '',
    avgViewers: source.avg_viewers || '',
    fanGrowthRate: source.fan_growth_rate || '',
    conversionRate: source.live_conversion_rate || '',
    avgGmv: source.avg_gmv || '',
    unitPrice: source.avg_order_value || '',
    last12MonthGmv: source.last_12m_gmv || '',
    category: source.vertical_track || ''
  };
}

export function createEmptyOrganizationAssetAnchorMetric() {
  return {
    id: `${Date.now()}-${Math.random()}`,
    streamerProfileId: '',
    platformAccounts: '',
    anchorName: '',
    totalFans: '',
    totalViews: '',
    liveSessions: '',
    annualProfit: '',
    avgViewers: '',
    fanGrowthRate: '',
    conversionRate: '',
    avgGmv: '',
    unitPrice: '',
    last12MonthGmv: '',
    category: ''
  };
}

export function createEmptyOrganizationAssetForm(preset = {}) {
  return {
    companyName: preset.companyName || '',
    creditCode: preset.creditCode || '',
    assetType: '机构资产',
    registerCapital: '',
    paidCapital: '',
    shareholders: '',
    financeHistory: '',
    relatedCompanies: '',
    foundedAt: '',
    staffCount: '',
    anchorCount: '',
    businessScopes: [],
    corePlatforms: [],
    certificates: [],
    officeAddress: '',
    honors: '',
    step2: {
      signedAnchorCount: '',
      exclusiveAnchorCount: '',
      fullTimeContractCount: '',
      businessContractCount: '',
      topAnchorCount: '',
      midAnchorCount: '',
      growthAnchorCount: '',
      coverCategories: [],
      incubationDescription: '',
      avgIncubationCost: '',
      totalIncubationCost: ''
    },
    step3: {
      assetValuation: '',
      valuationDescription: '',
      monthlyUpdateCount: '',
      viralRate: '',
      avgStaySeconds: '',
      fanInteractionRate: '',
      liveInteractionRate: '',
      crossPlatformAccountCount: '',
      selectionProcess: '',
      contentCompliance: ''
    },
    step4: {
      ownershipSubjects: [],
      authorizationFiles: [],
      usageFiles: [],
      incomeFiles: [],
      copyrightFiles: [],
      commercialFiles: []
    },
    step5: {
      anchorMetrics: [createEmptyOrganizationAssetAnchorMetric()],
      summary: {
        annualGMV: '',
        annualRevenue: '',
        annualNetProfit: '',
        last12MonthsSessions: '',
        avgViewersCount: '',
        totalFanCoverage: ''
      },
      incomeStructure: {
        commissionRatio: '',
        slotFeeRatio: '',
        brandIncomeRatio: '',
        otherIncomeRatio: '',
        costRatio: '',
        netProfitRatio: ''
      },
      forecast: {
        shortTermIncome6: '',
        lifecycleIncome3: '',
        forecastBasis: '',
        thirdPartySupport: ''
      }
    },
    step6: {
      shortTermIncome12: '',
      lifecycleIncome5: '',
      confirmAgreement: false
    }
  };
}

export function getDefaultOrganizationAssetOptions() {
  return {
    vertical_track: createOptionList(DEFAULT_OPTIONS.vertical_track),
    mcn_platform: createOptionList(DEFAULT_OPTIONS.mcn_platform),
    business_scope: createOptionList(DEFAULT_OPTIONS.business_scope),
    qualification: createOptionList(DEFAULT_OPTIONS.qualification),
    account_ownership_type: createOptionList(DEFAULT_OPTIONS.account_ownership_type)
  };
}

function normalizeOrganizationAssetForm(record, preset = {}) {
  const base = createEmptyOrganizationAssetForm(preset);
  const source = record && typeof record === 'object' ? record : {};
  const mcnData = source.mcn_data && typeof source.mcn_data === 'object' ? source.mcn_data : {};
  const basic = mcnData.basic && typeof mcnData.basic === 'object' ? mcnData.basic : {};
  const incubation = mcnData.incubation && typeof mcnData.incubation === 'object' ? mcnData.incubation : {};
  const contentInfluence = mcnData.content_influence && typeof mcnData.content_influence === 'object' ? mcnData.content_influence : {};
  const accountAssets = mcnData.account_assets && typeof mcnData.account_assets === 'object' ? mcnData.account_assets : {};
  const operation = mcnData.operation && typeof mcnData.operation === 'object' ? mcnData.operation : {};
  const commercial = mcnData.commercial && typeof mcnData.commercial === 'object' ? mcnData.commercial : {};
  const streamers = Array.isArray(operation.streamers) ? operation.streamers : [];

  return {
    ...base,
    companyName: basic.company_name || preset.companyName || base.companyName,
    creditCode: basic.credit_code || preset.creditCode || base.creditCode,
    assetType: source.asset_category || base.assetType,
    registerCapital: basic.registered_capital || '',
    paidCapital: basic.paid_in_capital || '',
    shareholders: basic.equity_structure || '',
    financeHistory: basic.financing_history || '',
    relatedCompanies: basic.affiliates || '',
    foundedAt: basic.established_at || '',
    staffCount: basic.employee_count || '',
    anchorCount: basic.signed_streamer_count || '',
    businessScopes: Array.isArray(basic.business_scope) ? basic.business_scope : [],
    corePlatforms: Array.isArray(basic.core_platforms) ? basic.core_platforms : [],
    certificates: Array.isArray(basic.qualifications) ? basic.qualifications : [],
    officeAddress: basic.office_location || '',
    honors: basic.honors || '',
    step2: {
      ...base.step2,
      signedAnchorCount: incubation.signed_streamer_total || '',
      exclusiveAnchorCount: incubation.exclusive_streamer_count || '',
      fullTimeContractCount: incubation.full_contract_count || '',
      businessContractCount: incubation.business_contract_count || '',
      topAnchorCount: incubation.top_streamer_count || '',
      midAnchorCount: incubation.waist_streamer_count || '',
      growthAnchorCount: incubation.growth_streamer_count || '',
      coverCategories: Array.isArray(incubation.verticals) ? incubation.verticals : [],
      incubationDescription: incubation.process_description || '',
      avgIncubationCost: incubation.avg_incubation_cost || '',
      totalIncubationCost: incubation.annual_incubation_investment || ''
    },
    step3: {
      ...base.step3,
      assetValuation: contentInfluence.asset_valuation || '',
      valuationDescription: contentInfluence.valuation_note || '',
      monthlyUpdateCount: contentInfluence.short_video_monthly_updates || '',
      viralRate: contentInfluence.short_video_hit_rate || '',
      avgStaySeconds: contentInfluence.avg_live_stay_duration || '',
      fanInteractionRate: contentInfluence.fan_interaction_rate || '',
      liveInteractionRate: contentInfluence.live_interaction_rate || '',
      crossPlatformAccountCount: contentInfluence.cross_platform_account_count || '',
      selectionProcess: contentInfluence.selection_review_process || '',
      contentCompliance: contentInfluence.compliance_mechanism || ''
    },
    step4: {
      ownershipSubjects: Array.isArray(accountAssets.ownership_types) ? accountAssets.ownership_types : [],
      authorizationFiles: normalizeFileList(accountAssets.ownership_auth_files),
      usageFiles: normalizeFileList(accountAssets.use_right_auth_files),
      incomeFiles: normalizeFileList(accountAssets.revenue_right_auth_files),
      copyrightFiles: normalizeFileList(accountAssets.copyright_files),
      commercialFiles: normalizeFileList(accountAssets.brand_cooperation_files)
    },
    step5: {
      anchorMetrics: streamers.length
        ? streamers.map(normalizeAnchorMetric)
        : [createEmptyOrganizationAssetAnchorMetric()],
      summary: {
        annualGMV: operation.annual_gmv || '',
        annualRevenue: operation.annual_revenue || '',
        annualNetProfit: operation.annual_net_profit || '',
        last12MonthsSessions: operation.last_12m_live_sessions || '',
        avgViewersCount: operation.avg_viewers || '',
        totalFanCoverage: operation.total_fan_coverage || ''
      },
      incomeStructure: {
        commissionRatio: operation.commission_income_ratio || '',
        slotFeeRatio: operation.slot_fee_income_ratio || '',
        brandIncomeRatio: operation.brand_marketing_income_ratio || '',
        otherIncomeRatio: operation.other_income_ratio || '',
        costRatio: operation.operating_cost_ratio || '',
        netProfitRatio: operation.net_profit_ratio || ''
      },
      forecast: {
        shortTermIncome6: operation.short_term_6m || '',
        lifecycleIncome3: operation.lifecycle_3y || '',
        forecastBasis: operation.revenue_basis || '',
        thirdPartySupport: operation.third_party_support || ''
      }
    },
    step6: {
      ...base.step6,
      shortTermIncome12: commercial.short_term || '',
      lifecycleIncome5: commercial.lifecycle || ''
    }
  };
}

function normalizeOrganizationAssetRecord(record) {
  const source = record && typeof record === 'object' ? record : {};
  const basic = source.mcn_data && source.mcn_data.basic && typeof source.mcn_data.basic === 'object'
    ? source.mcn_data.basic
    : {};
  const report = source.report && typeof source.report === 'object' ? source.report : {};

  return {
    id: source.id,
    applicationNo: source.evaluate_no || '--',
    assetName: source.asset_name || basic.company_name || '--',
    assetCategory: source.asset_category || '--',
    submittedAt: formatDateTime(source.submitted_at || source.created_at || source.updated_at),
    status: source.status || '',
    statusLabel: source.status_text || STATUS_LABEL_MAP[source.status] || source.status || '--',
    reportUrl: source.report_url || null,
    reportChainId: report.chain_id || source.report_chain_id || '',
    raw: source
  };
}

function buildOrganizationAssetPayload(form, sourceEvaluationId) {
  const source = form && typeof form === 'object' ? form : createEmptyOrganizationAssetForm();
  const step2 = source.step2 || {};
  const step3 = source.step3 || {};
  const step4 = source.step4 || {};
  const step5 = source.step5 || {};
  const step6 = source.step6 || {};
  const summary = step5.summary || {};
  const incomeStructure = step5.incomeStructure || {};
  const forecast = step5.forecast || {};

  const payload = {
    asset_name: source.companyName || '机构资产',
    asset_category: source.assetType || '机构资产',
    mcn_data: {
      basic: {
        company_name: source.companyName,
        credit_code: source.creditCode,
        registered_capital: source.registerCapital,
        paid_in_capital: source.paidCapital,
        equity_structure: source.shareholders,
        financing_history: source.financeHistory,
        affiliates: source.relatedCompanies,
        established_at: source.foundedAt,
        employee_count: source.staffCount,
        signed_streamer_count: source.anchorCount,
        business_scope: Array.isArray(source.businessScopes) ? source.businessScopes : [],
        core_platforms: Array.isArray(source.corePlatforms) ? source.corePlatforms : [],
        qualifications: Array.isArray(source.certificates) ? source.certificates : [],
        office_location: source.officeAddress,
        honors: source.honors
      },
      incubation: {
        signed_streamer_total: step2.signedAnchorCount,
        exclusive_streamer_count: step2.exclusiveAnchorCount,
        full_contract_count: step2.fullTimeContractCount,
        business_contract_count: step2.businessContractCount,
        top_streamer_count: step2.topAnchorCount,
        waist_streamer_count: step2.midAnchorCount,
        growth_streamer_count: step2.growthAnchorCount,
        verticals: Array.isArray(step2.coverCategories) ? step2.coverCategories : [],
        process_description: step2.incubationDescription,
        avg_incubation_cost: step2.avgIncubationCost,
        annual_incubation_investment: step2.totalIncubationCost
      },
      content_influence: {
        asset_valuation: step3.assetValuation,
        valuation_note: step3.valuationDescription,
        short_video_monthly_updates: step3.monthlyUpdateCount,
        short_video_hit_rate: step3.viralRate,
        avg_live_stay_duration: step3.avgStaySeconds,
        fan_interaction_rate: step3.fanInteractionRate,
        live_interaction_rate: step3.liveInteractionRate,
        cross_platform_account_count: step3.crossPlatformAccountCount,
        selection_review_process: step3.selectionProcess,
        compliance_mechanism: step3.contentCompliance
      },
      account_assets: {
        ownership_types: Array.isArray(step4.ownershipSubjects) ? step4.ownershipSubjects : [],
        ownership_auth_files: extractFileUrls(step4.authorizationFiles),
        use_right_auth_files: extractFileUrls(step4.usageFiles),
        revenue_right_auth_files: extractFileUrls(step4.incomeFiles),
        copyright_files: extractFileUrls(step4.copyrightFiles),
        brand_cooperation_files: extractFileUrls(step4.commercialFiles)
      },
      operation: {
        streamers: (Array.isArray(step5.anchorMetrics) ? step5.anchorMetrics : []).map(item => ({
          streamer_profile_id: item.streamerProfileId,
          platform_id: item.platformAccounts,
          name: item.anchorName,
          total_fans: item.totalFans,
          total_clicks: item.totalViews,
          last_year_live_sessions: item.liveSessions,
          last_year_profit: item.annualProfit,
          avg_viewers: item.avgViewers,
          fan_growth_rate: item.fanGrowthRate,
          live_conversion_rate: item.conversionRate,
          avg_gmv: item.avgGmv,
          avg_order_value: item.unitPrice,
          last_12m_gmv: item.last12MonthGmv,
          vertical_track: item.category
        })),
        annual_gmv: summary.annualGMV,
        annual_revenue: summary.annualRevenue,
        annual_net_profit: summary.annualNetProfit,
        last_12m_live_sessions: summary.last12MonthsSessions,
        avg_viewers: summary.avgViewersCount,
        total_fan_coverage: summary.totalFanCoverage,
        commission_income_ratio: incomeStructure.commissionRatio,
        slot_fee_income_ratio: incomeStructure.slotFeeRatio,
        brand_marketing_income_ratio: incomeStructure.brandIncomeRatio,
        other_income_ratio: incomeStructure.otherIncomeRatio,
        operating_cost_ratio: incomeStructure.costRatio,
        net_profit_ratio: incomeStructure.netProfitRatio,
        short_term_6m: forecast.shortTermIncome6,
        lifecycle_3y: forecast.lifecycleIncome3,
        revenue_basis: forecast.forecastBasis,
        third_party_support: forecast.thirdPartySupport
      },
      commercial: {
        short_term: step6.shortTermIncome12,
        lifecycle: step6.lifecycleIncome5
      }
    }
  };

  if (sourceEvaluationId) {
    payload.source_evaluation_id = sourceEvaluationId;
  }

  return payload;
}

function normalizeListPayload(payload, fallbackPerPage) {
  if (Array.isArray(payload)) {
    return {
      items: payload.map(normalizeOrganizationAssetRecord),
      total: payload.length,
      currentPage: 1,
      perPage: fallbackPerPage
    };
  }

  const rows = Array.isArray(payload && payload.data) ? payload.data : [];
  const meta = payload && payload.meta && typeof payload.meta === 'object' ? payload.meta : payload || {};
  const activeDraft = meta.active_draft && typeof meta.active_draft === 'object' ? meta.active_draft : null;
  const activeDraftReport = activeDraft && activeDraft.report && typeof activeDraft.report === 'object'
    ? activeDraft.report
    : {};

  return {
    items: rows.map(normalizeOrganizationAssetRecord),
    total: Number(meta.total) || rows.length,
    currentPage: Number(meta.current_page || meta.page) || 1,
    perPage: Number(meta.per_page) || fallbackPerPage,
    activeDraft: activeDraft ? {
      reportChainId: activeDraftReport.chain_id || activeDraft.report_chain_id || '',
      sourceEvaluationId: activeDraft.source_evaluation_id || ''
    } : null
  };
}

export async function getOrganizationAssetOptions() {
  const fallback = getDefaultOrganizationAssetOptions();
  const response = await getAssetEvaluationOptions();
  const data = getResponseData(response);
  const source = data && typeof data === 'object' ? data : {};

  return {
    vertical_track: createOptionList(source.vertical_track && source.vertical_track.length ? source.vertical_track : fallback.vertical_track),
    mcn_platform: createOptionList(source.mcn_platform && source.mcn_platform.length ? source.mcn_platform : fallback.mcn_platform),
    business_scope: createOptionList(source.business_scope && source.business_scope.length ? source.business_scope : fallback.business_scope),
    qualification: createOptionList(source.qualification && source.qualification.length ? source.qualification : fallback.qualification),
    account_ownership_type: createOptionList(source.account_ownership_type && source.account_ownership_type.length ? source.account_ownership_type : fallback.account_ownership_type)
  };
}

export async function getOrganizationAssetApplications(params = { per_page: 15 }) {
  const response = await getAssetEvaluationList(params);
  const data = getResponseData(response);
  return normalizeListPayload(data, Number(params.per_page) || 15);
}

export async function getOrganizationAssetApplicationById(id, preset = {}) {
  const response = await getAssetEvaluationDetail(id);
  const data = getResponseData(response);
  if (!data) {
    return null;
  }

  return {
    ...normalizeOrganizationAssetRecord(data),
    form: normalizeOrganizationAssetForm(data, preset)
  };
}

export async function getOrganizationAssetApplicationDraft(preset = {}) {
  const response = await getAssetEvaluationDraft();
  const data = getResponseData(response);
  if (!data) {
    return null;
  }

  return {
    form: normalizeOrganizationAssetForm(data, preset),
    sourceEvaluationId: data.source_evaluation_id || '',
    raw: data
  };
}

export async function saveOrganizationAssetApplicationDraft(form, sourceEvaluationId) {
  const response = await saveAssetEvaluationDraft(buildOrganizationAssetPayload(cloneDeep(form), sourceEvaluationId));
  const data = getResponseData(response);
  return data ? normalizeOrganizationAssetForm(data) : null;
}

export async function submitOrganizationAssetApplication(form, sourceEvaluationId) {
  const response = await submitAssetEvaluation(buildOrganizationAssetPayload(cloneDeep(form), sourceEvaluationId));
  const data = getResponseData(response);
  if (!data) {
    return null;
  }

  return {
    ...normalizeOrganizationAssetRecord(data),
    form: normalizeOrganizationAssetForm(data)
  };
}
